<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import type { FormElementBase, ItemDefinition } from "~/api";

import {
  ComplianceItemFormElement,
  FormDefinition,
  FormElementType,
  SpecialQuestion,
} from "~/api";
import { type FormElementDefault } from "~/NewFormBuilder/components/FormComponents/defaultComponents";

import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const myInfoStore = useMyInfoStore();

const props = defineProps<{
  formComponents: FormElementBase[];
  form: FormDefinition;
  readOnly?: boolean;
}>();

// providing a default value
const isDragging = defineModel<boolean>("isDragging");

const emit = defineEmits<{
  (e: "component", payload: FormElementDefault<FormElementBase>): void;
}>();

const handleOnDrag = (
  e: DragEvent,
  component: FormElementDefault<ComplianceItemFormElement>,
) => {
  // To fix chrome bug. See:
  // https://stackoverflow.com/questions/14203734/dragend-dragenter-and-dragleave-firing-off-immediately-when-i-drag#20733870
  setTimeout(() => {
    isDragging.value = e.type === "dragstart";
  }, 10);

  if (e.dataTransfer) {
    // can't use dataTransfer.setData() because the tinyMCE editor will accept it and we don't want to drop there
    emit("component", component);
    e.dataTransfer.effectAllowed = "copy";
  }
};

const usedItemDefinitionGuids = computed(() => {
  return props.formComponents
    .filter((component) => component.type === FormElementType.COMPLIANCE_ITEM)
    .map(
      (component: ComplianceItemFormElement) =>
        component.properties.itemDefinitionGuid,
    );
});

// TODO: we need to update this to only fetch the ones in an associated checklist
type ChecklistComplianceComponents = {
  guid: string;
  name: string;
  items: FormElementDefault<ComplianceItemFormElement>[];
};

const allChecklists = ref<ChecklistComplianceComponents[]>([]);

const defaultBaseProperties = {
  isOptional: false,
  showOptionality: false,
  isReadOnly: false,
  title: null,
  indentLevel: 0,
  sensitivity: {},
  specialQuestion: SpecialQuestion.NONE,
  comment: null,
};

// map all the compliance items to ComplianceItemFormElement (form components)
const convertItemsToFormComponents = (
  complianceItems: ItemDefinition[] | null,
): FormElementDefault<ComplianceItemFormElement>[] => {
  if (!complianceItems) return [];

  const components = complianceItems.map((item) => {
    const c: FormElementDefault<ComplianceItemFormElement> = {
      type: FormElementType.COMPLIANCE_ITEM,
      label: item.name,
      questionCode: item.name.replace(/\s/g, ""),
      events: [],
      properties: {
        ...defaultBaseProperties,
        itemDefinitionGuid: item.guid,
      },
    };
    return c;
  });

  return components;
};

onMounted(async () => {
  // fetch all the compliance items
  await apiClient.checklistDefinition
    .getAllChecklistsForWorkflow(props.form.guid, myInfoStore.currentSysOrgGuid)
    .then((res) => {
      allChecklists.value = res.map((checklist) => {
        const cl: ChecklistComplianceComponents = {
          guid: checklist.guid,
          name: checklist.name ?? "Unnamed Checklist",
          items: convertItemsToFormComponents(checklist.items),
        };

        return cl;
      });
    })
    .catch(showError);

  // convertItemsToFormComponents(allComplianceItems.value);
});
</script>

<template id="cpldk-form-designer-actions">
  <div class="palette">
    <!-- <div class="finder-header">
        <span class="finder-title">
          <el-tooltip content="" placement="left" :show-after="1000">
            <template #content>
              The listing of available form elements<br />
              Search for elements by their name<br />
              Drag and drop elements into your form to add them
            </template>
            <el-icon>
              <el-icon-info-filled />
            </el-icon>
          </el-tooltip>
        </span>
        <el-input
          v-model="elementSearch"
          size="small"
          clearable
          class="finder-search"
          placeholder="Search..."
        ></el-input>
      </div> -->
    <div class="designerPalette">
      <div v-if="allChecklists.length">
        <div
          v-for="cl in allChecklists"
          :key="cl.guid"
          class="designerPalette-group"
        >
          <div class="designerPalette-group-name">{{ cl.name }} Items</div>
          <div v-if="cl.items.length > 0" class="designerPalette-item-group">
            <div
              v-for="component in cl.items"
              :key="component.questionCode"
              class="designerPalette-item"
              :draggable="!props.readOnly"
              :class="{
                'used-compliance-item': usedItemDefinitionGuids.includes(
                  component.properties.itemDefinitionGuid,
                ),
                'readonly-item': props.readOnly,
              }"
              @dragstart="!props.readOnly && handleOnDrag($event, component)"
              @dragend="!props.readOnly && handleOnDrag($event, component)"
            >
              <div class="designerPalette-item-name">
                <i :class="`mdi mdi-list-box-outline`"></i>
                {{ component.label }}
              </div>
            </div>
          </div>
          <div v-else class="designerPalette-group-none">
            <div>No compliance items available</div>
          </div>
        </div>
      </div>

      <div v-else class="designerPalette-group">
        <div class="designerPalette-group-none">No checklists added</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.designerPalette {
  padding: 10px 0px;
  overflow: auto;
  h2 {
    font-size: large;
  }

  .used-compliance-item {
    background-color: rgb(242, 236, 236);
    pointer-events: none;
  }
  .designerPalette-group {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 3px;

    overflow: auto;
    padding: 0px 15px 15px 20px;

    .designerPalette-group-name {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 3px;
      width: 100%;
      color: #757575;
      font-weight: normal;
      //border-bottom: 2px solid #ccc;
    }

    .designerPalette-group-none {
      width: 100%;
      text-align: center;
      color: #ccc;
      margin-top: 5px;
    }

    .designerPalette-item-group {
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 10px 15px;
      margin: 0 0 10px 15px;
    }

    .designerPalette-item {
      border-radius: 5px;
      border: 1px solid #ccc;
      box-sizing: border-box;
      padding: 1px 6px;
      margin: 2px 0;
      user-select: none;
      cursor: pointer;

      &:hover {
        border-color: var(--el-color-primary);
      }
    }
    .designerPalette-item-name {
      i {
        color: var(--el-color-primary);
      }
    }

    .used-compliance-item {
      background-color: rgb(242, 236, 236);
      pointer-events: none;
    }

    .readonly-item {
      background-color: #f5f5f5;
      cursor: default !important;
      opacity: 0.8;

      &:hover {
        border-color: #ccc;
      }
    }
  }
}
</style>
